import { useState, useEffect } from 'react'
import Button from '../../../components/common/Button/Button'

/**
 * RejectModal Component
 * Modal for admin to enter rejection comments
 *
 * @param {boolean} isOpen - Whether modal is open
 * @param {function} onClose - Close modal callback
 * @param {function} onSubmit - Submit rejection callback
 * @param {object} request - Request being rejected (for display)
 * @param {boolean} isLoading - Whether form is submitting
 */
const RejectModal = ({ isOpen, onClose, onSubmit, request, isLoading = false }) => {
  const [comments, setComments] = useState('')
  const [error, setError] = useState('')

  const commentsMaxLength = 500

  useEffect(() => {
    if (!isOpen) {
      resetForm()
    }
  }, [isOpen])

  const resetForm = () => {
    setComments('')
    setError('')
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('')

    // Validate
    if (!comments.trim()) {
      setError('Rejection comments are required')
      return
    }

    if (comments.length > commentsMaxLength) {
      setError(`Comments cannot exceed ${commentsMaxLength} characters`)
      return
    }

    // Submit
    onSubmit(comments.trim())
  }

  const handleClose = () => {
    if (!isLoading) {
      resetForm()
      onClose()
    }
  }

  const handleBackdropClick = (e) => {
    if (e.target.classList.contains('psyber-modal-overlay')) {
      handleClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="psyber-modal-overlay" onClick={handleBackdropClick}>
      <div className="psyber-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
        {/* Modal Header */}
        <div className="psyber-modal-header">
          <h3 style={{
            margin: 0,
            fontSize: 'var(--psyber-font-size-2xl)',
            color: 'var(--psyber-text)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <i className="fas fa-times-circle" style={{ color: 'var(--psyber-error)' }}></i>
            Reject Request
          </h3>
          <button
            onClick={handleClose}
            disabled={isLoading}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              color: 'var(--psyber-text-secondary)',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              padding: '0.25rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '2rem',
              height: '2rem',
              borderRadius: 'var(--psyber-radius-sm)',
              transition: 'var(--psyber-transition-fast)'
            }}
            onMouseEnter={(e) => {
              if (!isLoading) e.currentTarget.style.background = 'var(--psyber-card-hover)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'none'
            }}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit}>
          {/* Request Info */}
          {request && (
            <div style={{
              marginBottom: '1.5rem',
              padding: '1rem',
              background: 'var(--psyber-card-hover)',
              borderRadius: 'var(--psyber-radius-md)',
              border: '1px solid var(--psyber-card-border)'
            }}>
              <div style={{ fontSize: 'var(--psyber-font-size-sm)', color: 'var(--psyber-text-secondary)', marginBottom: '0.5rem' }}>
                Rejecting request from:
              </div>
              <div style={{ fontSize: 'var(--psyber-font-size-base)', color: 'var(--psyber-text)', fontWeight: 600 }}>
                <i className="fas fa-user"></i> {request.employeeName}
              </div>
              <div style={{ fontSize: 'var(--psyber-font-size-sm)', color: 'var(--psyber-text-secondary)', marginTop: '0.25rem' }}>
                {request.leaveType} • {request.duration} day{request.duration !== 1 ? 's' : ''}
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div style={{
              marginBottom: '1.5rem',
              padding: '1rem',
              background: 'var(--psyber-error-bg)',
              border: '2px solid var(--psyber-error)',
              borderRadius: 'var(--psyber-radius-md)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <i className="fas fa-exclamation-circle" style={{ fontSize: '1.5rem', color: 'var(--psyber-error)' }}></i>
              <span style={{ color: 'var(--psyber-error)', fontWeight: 600 }}>{error}</span>
            </div>
          )}

          {/* Comments Field */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label
              htmlFor="comments"
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: 'var(--psyber-font-size-sm)',
                fontWeight: 600,
                color: 'var(--psyber-text)'
              }}
            >
              <i className="fas fa-comment"></i> Rejection Comments *
            </label>
            <textarea
              id="comments"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Explain why this request is being rejected..."
              disabled={isLoading}
              maxLength={commentsMaxLength}
              className="psyber-input"
              rows={6}
              style={{
                width: '100%',
                resize: 'vertical',
                minHeight: '120px'
              }}
              required
              autoFocus
            />
            <div style={{
              marginTop: '0.25rem',
              fontSize: 'var(--psyber-font-size-xs)',
              color: comments.length > commentsMaxLength * 0.9 ? 'var(--psyber-warning)' : 'var(--psyber-text-muted)',
              textAlign: 'right'
            }}>
              {comments.length}/{commentsMaxLength} characters
            </div>
          </div>

          {/* Help Text */}
          <div style={{
            marginBottom: '1.5rem',
            padding: '0.75rem',
            background: 'var(--psyber-warning-bg)',
            border: '1px solid var(--psyber-warning)',
            borderRadius: 'var(--psyber-radius-md)',
            fontSize: 'var(--psyber-font-size-sm)',
            color: 'var(--psyber-text-secondary)'
          }}>
            <i className="fas fa-info-circle" style={{ color: 'var(--psyber-warning)', marginRight: '0.5rem' }}></i>
            <strong>Note:</strong> The employee will see these comments. Please be professional and clear about the reason for rejection.
          </div>

          {/* Modal Footer */}
          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'flex-end',
            paddingTop: '1rem',
            borderTop: '1px solid var(--psyber-card-border)'
          }}>
            <Button
              type="button"
              variant="ghost"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              icon="fas fa-ban"
              loading={isLoading}
              disabled={isLoading || !comments.trim()}
              style={{
                background: 'var(--psyber-error)',
                borderColor: 'var(--psyber-error)'
              }}
            >
              {isLoading ? 'Rejecting...' : 'Reject Request'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default RejectModal
